export type MemberScope =
  | "workflows:read"
  | "workflows:read_all"
  | "workflows:write"
  | "workflows:write_all"
  | "employees:read"
  | "employees:write"
  | "forms:read_all"
  | "forms:write_all"
  | "datasets:read_all"
  | "datasets:write_all";

type ScopeBreadth = "none" | "restricted" | "all";

export type WorkflowsCapability = {
  capability: string;
  restricted: MemberScope;
  all: MemberScope;
};

export const WORKFLOWS_CAPABILITIES: WorkflowsCapability[] = [
  { capability: "View", restricted: "workflows:read", all: "workflows:read_all" },
  { capability: "Manage", restricted: "workflows:write", all: "workflows:write_all" }
];

export const CHECKBOX_SCOPE_GROUPS: { resource: string; scopes: { value: MemberScope; label: string }[] }[] = [
  {
    resource: "Employees",
    scopes: [
      { value: "employees:read", label: "View" },
      { value: "employees:write", label: "Manage" }
    ]
  },
  {
    resource: "Forms",
    scopes: [
      { value: "forms:read_all", label: "View" },
      { value: "forms:write_all", label: "Manage" }
    ]
  },
  {
    resource: "Datasets",
    scopes: [
      { value: "datasets:read_all", label: "View" },
      { value: "datasets:write_all", label: "Manage" }
    ]
  }
];

export const MEMBER_SCOPES: MemberScope[] = [
  "workflows:read",
  "workflows:read_all",
  "workflows:write",
  "workflows:write_all",
  "employees:read",
  "employees:write",
  "forms:read_all",
  "forms:write_all",
  "datasets:read_all",
  "datasets:write_all"
];

// users.updateScopes replaces the caller's whole scopes array, but this UI only understands
// (and should only ever write) the member-manageable subset above — other admin-area scopes
// (settings, companydata, etc.) must round-trip untouched. Use this to split a user's full
// scopes into the editable part before showing it here, and to preserve the rest on save.
export const isMemberScope = (scope: string): scope is MemberScope =>
  (MEMBER_SCOPES as string[]).includes(scope);

export const getWorkflowsBreadth = (
  scopes: MemberScope[],
  capability: WorkflowsCapability
): ScopeBreadth => {
  if (scopes.includes(capability.all)) return "all";
  if (scopes.includes(capability.restricted)) return "restricted";
  return "none";
};

export const describeScopes = (scopes: MemberScope[]): string => {
  const parts: string[] = [];

  const workflowsGrants = WORKFLOWS_CAPABILITIES.map(capability => {
    const breadth = getWorkflowsBreadth(scopes, capability);
    if (breadth === "none") return null;
    return breadth === "all" ? capability.capability : `${capability.capability} (restricted)`;
  }).filter((grant): grant is string => grant !== null);
  if (workflowsGrants.length) {
    parts.push(`Workflows: ${workflowsGrants.join(", ")}`);
  }

  CHECKBOX_SCOPE_GROUPS.forEach(group => {
    const granted = group.scopes
      .filter(scope => scopes.includes(scope.value))
      .map(scope => scope.label);
    if (granted.length) {
      parts.push(`${group.resource}: ${granted.join(", ")}`);
    }
  });

  return parts.length ? parts.join(" · ") : "No access";
};
